/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 依頼のクローズ確認モーダル。解決（closed）操作時に開き、調査メモ（memo）と調査ログ（notes）から AI 生成した対応結論の下書きを初期値に、送信前の承認・編集をさせる。「送信して解決」で結論付き解決、「報告なしで解決」で結論なし解決。承認なしに AI 文がスレッドへ出ないための人手ゲート
 * @graph-connects useDraftOpsCloseReport [calls] モーダルを開いたとき調査メモ・調査ログから対応結論の下書きを AI 生成
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import type { OpsCategory } from '@cortex/ops-core';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { useDraftOpsCloseReport, useSetOpsRequestWorkMinutes } from '../../../hooks/useOps.js';
import { opsWorkMinutesOptions } from './OpsPrimitives.js';

/**
 * 対応結論の最大文字数。サーバー側 `OpsStateEditSchema.closeReport` の `.max(4000)` と一致させ、
 * 4000 文字超の入力を UI 段階で防いで Zod バリデーション失敗（原因不明の解決失敗）を回避する。
 * @graph-connects none
 */
const OPS_CLOSE_REPORT_MAX_LENGTH = 4000;

/** OpsCloseReportModal の props。 */
export interface OpsCloseReportModalProps {
  /** 対象依頼の id（下書き生成・作業時間の保存に使う）。 */
  requestId: string;
  /** 対象依頼の種別。作業時間の目安をラベルに出すために使う。 */
  requestCategory: OpsCategory;
  /** モーダルの表示状態。 */
  open: boolean;
  /** 閉じる（キャンセル）。解決はしない。 */
  onClose: () => void;
  /** 解決を確定する。closeReport=結論文字列（送信して解決）/ null（報告なしで解決）。 */
  onResolve: (closeReport: string | null) => void;
  /** 解決 PATCH 実行中か（ボタンの disable 用）。 */
  isResolving: boolean;
  /** 直前の解決 PATCH が失敗したか（モーダルを開いたままエラー表示・再送信させる）。 */
  resolveFailed: boolean;
}

/**
 * クローズ確認モーダル。開いたら調査メモ・調査ログから対応結論の下書きを AI 生成して textarea の
 * 初期値にし、人手で承認・編集してから解決する。調査記録が無く下書きが空なら入力を促し、空のままでは
 * 「送信して解決」を無効化する（結論を送るなら中身必須）。「報告なしで解決」は結論なしで解決する。
 * あわせて作業時間（分）を任意で記録する。既定は未記録で、種別ごとの目安はラベルに出すだけで初期選択
 * しない。選ばれていれば解決前に保存し、保存に失敗したら解決しない（入力を失わせないため）。
 * @graph-connects useSetOpsRequestWorkMinutes [calls] 選択された作業時間を保存してからクローズする
 * @graph-connects opsWorkMinutesOptions [calls] 作業時間ドロップダウンの選択肢を組み立てる
 */
export function OpsCloseReportModal({
  requestId,
  requestCategory,
  open,
  onClose,
  onResolve,
  isResolving,
  resolveFailed,
}: OpsCloseReportModalProps): ReactNode {
  const { t } = useTranslation();
  const [report, setReport] = useState('');
  // 作業時間（分）。既定は未記録（''）。目安は選択肢のラベルで示すだけで、初期選択はしない
  // （自動で入れると、誰も確認していない値が実績として BQ に載る）。
  const [workMinutes, setWorkMinutes] = useState('');
  const setWorkMinutesMutation = useSetOpsRequestWorkMinutes();
  // この open セッションで保存済みの作業時間。クローズ再試行時の二重 PATCH を防ぐ。
  const savedWorkMinutesRef = useRef('');
  const draftMutation = useDraftOpsCloseReport();
  const { mutate: draftMutate, reset: draftReset } = draftMutation;
  // この open セッションで既に下書き生成したか。再レンダーで effect が再走しても再生成・入力上書きを防ぐ。
  const draftedRef = useRef(false);
  // open セッションの世代。close で採番し直し、古い open の下書き応答が新しい open の入力を上書きするのを防ぐ
  // （close→即 reopen で前回の in-flight 応答が遅れて到着しても、世代不一致なら無視する）。
  const openGenRef = useRef(0);

  // 開いたときに 1 度だけ下書きを生成して初期値にする。閉じたら入力と生成状態をリセットする（次回開時に再生成）。
  useEffect(() => {
    if (!open) {
      // 閉じた時点で世代を進め、in-flight の下書き応答を無効化する。
      openGenRef.current += 1;
      draftedRef.current = false;
      setReport('');
      // 作業時間も必ず未記録へ戻す。戻さないと「キャンセルして閉じる → 後で開き直して解決」で、
      // 一度捨てたはずの値がそのまま実測として BQ へ載る（既定が未記録である前提が崩れる）。
      setWorkMinutes('');
      savedWorkMinutesRef.current = '';
      draftReset();
      return;
    }
    if (draftedRef.current) return;
    draftedRef.current = true;
    const gen = openGenRef.current;
    draftMutate(
      { id: requestId },
      {
        onSuccess: ({ draft }) => {
          // 応答到着時点でまだ同じ open セッションのときだけ反映する（古い応答での上書きを防ぐ）。
          if (gen === openGenRef.current) setReport(draft);
        },
      },
    );
  }, [open, requestId, draftMutate, draftReset]);

  const isDrafting = draftMutation.isPending;
  const canSubmit = report.trim() !== '' && !isDrafting && !isResolving;

  const workMinutesOptions = opsWorkMinutesOptions(requestCategory, t);

  /**
   * 作業時間を記録してから解決する。未記録（空）ならそのまま解決する。
   *
   * 作業時間の保存に失敗したときは解決しない。任意項目なので「失敗したら黙って捨てて閉じる」でも
   * 動きはするが、入力した値が消えたことに気付けないため、モーダルを開いたままエラーを出す。
   */
  const resolveWith = (closeReport: string | null): void => {
    // 既に同じ値を保存済みなら再送しない。クローズ PATCH だけが失敗して利用者が再試行したとき、
    // 素直に再送すると同じ作業時間で 2 回 PATCH が走り、BQ ミラーに同内容の行が 2 本積む。
    if (workMinutes === '' || savedWorkMinutesRef.current === workMinutes) {
      onResolve(closeReport);
      return;
    }
    setWorkMinutesMutation.mutate(
      { id: requestId, workMinutes: Number(workMinutes) },
      {
        onSuccess: () => {
          savedWorkMinutesRef.current = workMinutes;
          onResolve(closeReport);
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('ops.detail.closeReport.title')}
      description={t('ops.detail.closeReport.description')}
      size="lg"
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={isResolving}>
            {t('ops.detail.closeReport.cancel')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              resolveWith(null);
            }}
            disabled={isResolving || isDrafting || setWorkMinutesMutation.isPending}
          >
            {t('ops.detail.closeReport.resolveWithoutReport')}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => {
              resolveWith(report.trim());
            }}
            disabled={!canSubmit || setWorkMinutesMutation.isPending}
          >
            {t('ops.detail.closeReport.resolveWithReport')}
          </Button>
        </div>
      }
    >
      <div className="space-y-2">
        {isDrafting ? (
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('ops.detail.closeReport.generating')}
          </p>
        ) : (
          draftMutation.isSuccess &&
          report === '' && (
            <p className="text-sm text-[var(--color-warning)]">
              {t('ops.detail.closeReport.noRecordHint')}
            </p>
          )
        )}
        {draftMutation.isError && (
          <p className="text-sm text-[var(--color-error)]">
            {t('ops.detail.closeReport.draftFailed')}
          </p>
        )}
        {resolveFailed && (
          <p className="text-sm text-[var(--color-error)]">
            {t('ops.detail.closeReport.resolveFailed')}
          </p>
        )}
        {setWorkMinutesMutation.isError && (
          <p className="text-sm text-[var(--color-error)]">
            {t('ops.detail.workMinutes.saveFailed')}
          </p>
        )}
        <div>
          <label
            htmlFor="ops-close-work-minutes"
            className="block text-xs font-semibold text-[var(--color-text-secondary)]"
          >
            {t('ops.detail.workMinutes.title')}
          </label>
          <Select
            id="ops-close-work-minutes"
            options={workMinutesOptions}
            value={workMinutes}
            onChange={(e) => {
              setWorkMinutes(e.target.value);
            }}
            disabled={isResolving || setWorkMinutesMutation.isPending}
            fullWidth
          />
        </div>
        <label
          htmlFor="ops-close-report"
          className="block text-xs font-semibold text-[var(--color-text-secondary)]"
        >
          {t('ops.detail.closeReport.label')}
        </label>
        <textarea
          id="ops-close-report"
          className="min-h-40 w-full resize-y rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-border-focus)] focus:outline-none"
          value={report}
          maxLength={OPS_CLOSE_REPORT_MAX_LENGTH}
          onChange={(e) => {
            setReport(e.target.value);
          }}
          placeholder={t('ops.detail.closeReport.placeholder')}
          disabled={isDrafting || isResolving}
        />
        <p className="text-right text-xs text-[var(--color-text-muted)]">
          {report.length} / {OPS_CLOSE_REPORT_MAX_LENGTH}
        </p>
      </div>
    </Modal>
  );
}
